import { fetchExplorerHolders, invalidateExplorerTokenHolders } from '@/lib/explorerHolders'
import { WAD } from '@/lib/launchpad/constants'
import { isEthAddress } from '@/lib/launchpad/contracts'
import { toHolderRows, type LaunchHolder } from '@/lib/launchpad/holders'
import type { SimPresale } from '@/lib/presale/types'

const cache = new Map<string, LaunchHolder[]>()
const fetched = new Set<string>()
const inflight = new Map<string, Promise<LaunchHolder[]>>()

function raisedWeiOf(presale: Pick<SimPresale, 'raisedWei'> | SimPresale) {
  return typeof presale?.raisedWei === 'bigint' ? presale.raisedWei : 0n
}

function cacheKey(presale: SimPresale) {
  return (presale.contractAddress || presale.id).toLowerCase()
}

function tokensFromEth(ethWei: bigint, tokensPerEthWei: bigint) {
  if (ethWei <= 0n)
    return 0n
  if (tokensPerEthWei <= 0n)
    return ethWei
  return (ethWei * tokensPerEthWei) / WAD
}

function pledgeRows(presale: SimPresale) {
  return Object.entries(presale.pledges ?? {}).flatMap(([address, ethWei]) => {
    const amount = tokensFromEth(ethWei, presale.tokensPerEthWei)
    return amount > 0n ? [{ address, amount }] : []
  })
}

function supplyFor(presale: Pick<SimPresale, 'totalSupplyWei'>) {
  const supply = presale?.totalSupplyWei
  return typeof supply === 'bigint' && supply > 0n ? supply : undefined
}

function remember(key: string, rows: LaunchHolder[], complete: boolean) {
  cache.set(key, rows)
  if (complete)
    fetched.add(key)
  else
    fetched.delete(key)
  return rows
}

const listeners = new Set<(key: string) => void>()

export function invalidatePresaleHolders(presale: Pick<SimPresale, 'id' | 'contractAddress' | 'tokenAddress'>) {
  const key = (presale.contractAddress || presale.id).toLowerCase()
  cache.delete(key)
  fetched.delete(key)
  inflight.delete(key)
  invalidateExplorerTokenHolders(presale.tokenAddress)
  invalidateExplorerTokenHolders(presale.contractAddress)
}

export function notifyPresaleHoldersChanged(presale: Pick<SimPresale, 'id' | 'contractAddress' | 'tokenAddress'>) {
  invalidatePresaleHolders(presale)
  const key = (presale.contractAddress || presale.id).toLowerCase()
  listeners.forEach(listener => listener(key))
}

export function subscribePresaleHoldersChanged(listener: (key: string) => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function snapshotPresaleHolders(presale: SimPresale): LaunchHolder[] {
  const key = cacheKey(presale)
  const cached = cache.get(key)
  if (cached)
    return cached
  if (isEthAddress(presale.contractAddress))
    return []
  if (raisedWeiOf(presale) === 0n)
    return []
  const seed = toHolderRows(
    pledgeRows(presale),
    presale.creatorAddress,
    supplyFor(presale),
    presale.tokenAddress,
  )
  cache.set(key, seed)
  return seed
}

export function hasLoadedPresaleHolders(presale: SimPresale) {
  return fetched.has(cacheKey(presale))
}

async function fetchPresaleHolders(presale: SimPresale): Promise<{ rows: LaunchHolder[], complete: boolean }> {
  const raised = raisedWeiOf(presale)
  if (raised === 0n && !isEthAddress(presale.tokenAddress))
    return { rows: [], complete: true }

  const token = isEthAddress(presale.tokenAddress) ? presale.tokenAddress : undefined
  const explorer = token
    ? await fetchExplorerHolders({ token })
    : undefined

  if (explorer?.holders.length) {
    const rows = toHolderRows(explorer.holders, presale.creatorAddress, supplyFor(presale), presale.tokenAddress)
    return { rows, complete: true }
  }

  const ethByAddress = new Map<string, bigint>()
  for (const [address, ethWei] of Object.entries(presale.pledges ?? {})) {
    if (ethWei > 0n)
      ethByAddress.set(address.toLowerCase(), ethWei)
  }
  for (const row of explorer?.contributions ?? []) {
    const key = row.address.toLowerCase()
    const current = ethByAddress.get(key) ?? 0n
    if (row.amount > current)
      ethByAddress.set(key, row.amount)
  }

  const liveRows: { address: string, amount: bigint }[] = []
  for (const [address, ethWei] of ethByAddress.entries()) {
    const pledgedTokens = tokensFromEth(ethWei, presale.tokensPerEthWei)
    if (pledgedTokens > 0n)
      liveRows.push({ address, amount: pledgedTokens })
  }

  const rows = toHolderRows(
    liveRows.length ? liveRows : pledgeRows(presale),
    presale.creatorAddress,
    supplyFor(presale),
    presale.tokenAddress,
  )
  const complete = rows.length > 0 || raised === 0n || Boolean(explorer)
  return { rows, complete }
}

export async function loadPresaleHolders(presale: SimPresale, options?: { force?: boolean, wallet?: string }): Promise<LaunchHolder[]> {
  if (options?.force)
    invalidatePresaleHolders(presale)
  const key = cacheKey(presale)
  if (!options?.force && fetched.has(key)) {
    const cached = cache.get(key) ?? snapshotPresaleHolders(presale)
    if (cached.length > 0 || raisedWeiOf(presale) === 0n)
      return cached
    fetched.delete(key)
  }
  const pending = inflight.get(key)
  if (pending)
    return pending

  const promise = fetchPresaleHolders(presale)
    .then((result) => {
      const kept = remember(key, result.rows, result.complete)
      inflight.delete(key)
      return kept
    })
    .catch((error) => {
      inflight.delete(key)
      throw error
    })
  inflight.set(key, promise)
  return promise
}
